// UsageCards — "Where used," per PRODUCT, as two independent cards (Used in
// Products / Placed in Projects). Same shape ProductsView's own Where-used
// pair renders, but self-contained: this owns its own fetch/state so a second
// mounting context (the Function screen, one per SELECTED PLACEMENT) doesn't
// need to thread usage state through a whole view.
//
// Alex, 2026-08-20: Product already answers "where else does THIS product
// appear." Function's placements grid shows what's placed HERE, which is a
// different question — select a placement, and THIS answers where that same
// product also appears elsewhere.
//
// CLOSED BY DEFAULT, LOADED ON OPEN — no "Load where-used" button; opening
// EITHER card's chevron loads the one shared fetch (both read the same
// query), and switching to a different placement while a card is already
// open re-fires it for the new product. Same rule as ProductsView's own
// Where-used pair; see that pair's `onOpen` comment in HierarchyWorkspace.jsx.
//
//   productId            — the product to report on; null renders nothing
//   onNavigateToProduct  — row click in "Used in Products" -> productId
//   onNavigateToPlacement — row click in "Placed in Projects" -> the placement row

import { useState, useEffect, useCallback } from 'react';
import DataGrid from './DataGrid.jsx';
import Card from './Card.jsx';
import { USED_IN_PRODUCTS_COLS, PLACED_IN_PROJECTS_COLS } from '../usage-cols.jsx';

// Card's own persisted-open pattern, hand-carried here rather than borrowed:
// these two cards need to react to `productId` changing (below) as well as
// their own click, which is exactly what Card's `open`/`onToggle` pair exists
// to hand back to a caller (2026-08-20) — the same key SHAPE Card itself
// writes (`inoviu:${storageKey}`), so persistence behaves identically to
// every other card in the app.
function usePersistedOpen(storageKey) {
  const openKey = `inoviu:${storageKey}`;
  const [open, setOpen] = useState(() => localStorage.getItem(openKey) === '1');
  const toggle = useCallback(() => {
    setOpen((o) => {
      const next = !o;
      localStorage.setItem(openKey, next ? '1' : '0');
      return next;
    });
  }, [openKey]);
  return [open, toggle];
}

export default function UsageCards({ productId, onNavigateToProduct, onNavigateToPlacement }) {
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openProducts, toggleOpenProducts] = usePersistedOpen('usage-cards:used-in-products');
  const [openPlacements, toggleOpenPlacements] = usePersistedOpen('usage-cards:placed-in-projects');

  // A new placement selected -> a new product -> the old usage no longer answers
  // the question on screen.
  useEffect(() => {
    setUsage(null);
    setLoading(false);
  }, [productId]);

  const load = useCallback(() => {
    if (!productId || loading) return;
    setLoading(true);
    fetch(`/api/products/${productId}/usage`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => setUsage(d || { placements: [], components: [] }))
      .finally(() => setLoading(false));
  }, [productId, loading]);

  // Fires whenever a card IS open and there is nothing to show it yet — covers
  // the click that opens a card AND a placement switch while one is already
  // open (which resets `usage` above but doesn't itself re-trigger a fetch).
  useEffect(() => {
    if ((openProducts || openPlacements) && usage == null && !loading) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId, openProducts, openPlacements, usage, loading]);

  if (!productId) return null;

  const body = (rows, columns, onRowClick, emptyMessage, storageKey) => (
    usage == null ? (
      <div style={{ fontSize: 'var(--fs-small)', color: 'var(--th-text-secondary)' }}>Loading usage…</div>
    ) : (
      <DataGrid rows={rows} columns={columns} onRowClick={onRowClick} emptyMessage={emptyMessage} storageKey={storageKey} />
    )
  );

  return (
    <>
      <Card
        storageKey="usage-cards:used-in-products"
        title="Used in Products"
        open={openProducts}
        onToggle={toggleOpenProducts}
        count={usage != null ? usage.components?.length ?? 0 : undefined}
      >
        {body(
          (usage?.components ?? []).map(c => ({ ...c, id: c.product_id })),
          USED_IN_PRODUCTS_COLS,
          onNavigateToProduct ? (row) => onNavigateToProduct(row.product_id) : undefined,
          'Not used as a component in any product.',
          'placement-usage-products',
        )}
      </Card>
      <Card
        storageKey="usage-cards:placed-in-projects"
        title="Placed in Projects"
        open={openPlacements}
        onToggle={toggleOpenPlacements}
        count={usage != null ? usage.placements?.length ?? 0 : undefined}
      >
        {body(
          (usage?.placements ?? []).map(p => ({ ...p, id: p.placement_id })),
          PLACED_IN_PROJECTS_COLS,
          onNavigateToPlacement ? (row) => onNavigateToPlacement(row) : undefined,
          'Not placed in any project.',
          'placement-usage-placements',
        )}
      </Card>
    </>
  );
}
